import { Autocomplete, Box, InputAdornment, ListItemIcon, Stack, TextField, Typography } from "@mui/material";
import type { JSX } from "react";
import { useMemo } from "react";
import type { MeasureDescriptor, MeasuresStructure } from "../../../../../../../../api/types/svTypes.ts";
import MetaIcon from "../../../common/MetaIcon.tsx";
import { useChartOfAccountsContext } from "./contexts/ChartOfAccountsContext";

interface MeasureSelectorProps {
  measures: MeasureDescriptor[];
  measuresStructure: MeasuresStructure;
  measure: MeasureDescriptor;
  readonly?: boolean;
  onSelect?: (measure: MeasureDescriptor) => void;
}
export default function MeasureSelector({ measures, measure, measuresStructure, onSelect }: MeasureSelectorProps) {
  const { resetSelectedAccounts } = useChartOfAccountsContext();

  const readOnly = onSelect === undefined;
  const measureList = useMemo(() => {
    const result: Measure[] = [];
    measures.forEach((measure) => {
      result.push(convertMeasure(measure, measuresStructure));
    });
    return result.sort(
      (a, b) =>
        measuresStructure.groups.findIndex((i) => i.caption === a.groupName) -
        measuresStructure.groups.findIndex((i) => i.caption === b.groupName)
    );
  }, [measures, measuresStructure]);

  return (
    <Stack spacing={1} useFlexGap sx={{ width: "100%" }}>
      <Typography variant="subtitle1">Measure</Typography>
      <Autocomplete
        disablePortal
        disableClearable
        options={measureList}
        readOnly={readOnly}
        getOptionLabel={getOptionLabel}
        value={measureList.find((m) => m.name === measure.name)}
        slotProps={{ listbox: { style: { fontSize: "13px" } } }}
        sx={{
          fieldset: { borderColor: "divider" },
          flex: 0.5,
          "& .MuiInputAdornment-positionStart": { ml: 0.75 },
          "& .MuiInputAdornment-root": { color: "secondary.light" },
          "& .MuiOutlinedInput-root:hover": { borderColor: "divider" },
        }}
        groupBy={groupBy}
        renderInput={(params) => (
          <TextField
            {...params}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <MetaIcon icon={measure.icon} />
                  </InputAdornment>
                ),
                endAdornment: readOnly ? null : params.slotProps.input.endAdornment,
              },
            }}
          />
        )}
        renderOption={({ key: _key, ...props }, option) => (
          <Box component="li" key={option.name} {...props}>
            <ListItemIcon
              sx={(theme) => ({
                minWidth: "20px",
                justifyContent: "center",
                color: option.custom ? theme.palette.info.main : theme.palette.secondary.light,
              })}
            >
              {option.icon}
            </ListItemIcon>
            <Typography
              variant="caption"
              sx={{ flexGrow: 1, pointerEvents: "none", pl: 1, color: (theme) => theme.palette.text.primary }}
            >
              {option.caption}
            </Typography>
          </Box>
        )}
        onChange={(_, value) => {
          const measure = measures.find((m) => m.name === value?.name);
          if (measure) {
            onSelect?.(measure);
            resetSelectedAccounts();
          }
        }}
      />
    </Stack>
  );
}

type Measure = { icon: JSX.Element; name: string; caption: string; groupName: string; custom: boolean };

function convertMeasure(measure: MeasureDescriptor, measuresStructure: MeasuresStructure): Measure {
  const groupName =
    measure.group || measuresStructure.groups.find((g) => g.names.includes(measure.name))?.caption || "";
  return {
    icon: <MetaIcon icon={measure.icon} />,
    name: measure.name,
    caption: measure.caption,
    groupName: groupName,
    custom: measure.custom,
  };
}

function getOptionLabel(option: Measure): string {
  return option.caption;
}
function groupBy(option: Measure): string {
  return option.groupName;
}
